import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GetSortTypesInput, GetSortTypesOutput } from 'src/models/GetSortTypes';
import { resetUiAlertState, setUiAlertState, setIsLoading, resetIsLoading } from 'src/store/common-slices/uiSlice';
import RIVBFFService from 'src/services/riv-bff-service';
import { AlertLevels } from 'src/models/alert-model';
import * as ErrorConstants from 'src/localization/string-ids/error-constants';
import { RIVApiMethods } from 'src/constants/api-method-constants';


export enum SortContainerPages {
    SELECT_SORT_TYPE_PAGE = 'SelectSortTypePage',
    SCAN_SOURCE_CONTAINER_PAGE = 'ScanSourceContainerPage',
    SCAN_DESTINATION_CONTAINER_PAGE = 'ScanDestinationContainerPage',
}

export interface SortContainerState {
    pageToRender: SortContainerPages;
    getSortTypesOutput?: GetSortTypesOutput;
    selectedSortType: string;
}

export const initialState: SortContainerState = {
    pageToRender: SortContainerPages.SELECT_SORT_TYPE_PAGE,
    getSortTypesOutput: undefined,
    selectedSortType: ''
}

export const getSortTypes = createAsyncThunk<GetSortTypesOutput, GetSortTypesInput>(
    'sort/getSortTypes',
    async (getSortTypesInput: GetSortTypesInput, { dispatch, rejectWithValue }) => {
        dispatch(resetUiAlertState());
        dispatch(setIsLoading());

        try {
            const getSortTypesOutput: GetSortTypesOutput = await RIVBFFService.triggerGetSortTypesAPI(getSortTypesInput);
            dispatch(resetIsLoading());
            return getSortTypesOutput;
        } catch (error) {
            dispatch(resetIsLoading());
            
            if (error && typeof error === 'object' && 'errorMessage' in error && 'errorStatus' in error) {
                const apiError = error as { errorMessage: string; errorStatus: number };
                
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertMessageString: apiError.errorMessage,
                    alertStatus: apiError.errorStatus,
                    UIAction: RIVApiMethods.GET_SORT_TYPES,
                    warehouse: getSortTypesInput.warehouseId,
                    associate: getSortTypesInput.associate,
                }));
                return rejectWithValue({
                    data: apiError.errorMessage,
                    status: apiError.errorStatus
                });
            }
            else if (error instanceof Error) {
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertMessageString: error.message
                }));
                return rejectWithValue(error.message);
            }
            else {
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertMessageString: ErrorConstants.UNKNOWN_ERROR
                }));
                return rejectWithValue(ErrorConstants.UNKNOWN_ERROR);
            }
        }
    }
);

const sortContainerSlice = createSlice({
    name: 'sortContainer',
    initialState,
    reducers: {
        setPageToRender: (state, action: PayloadAction<SortContainerPages>) => {
            state.pageToRender = action.payload;
        },
        setSelectedSortType: (state, action: PayloadAction<string>) => {
            state.selectedSortType = action.payload
        },
        resetSortationPageToRender: (state) => {
            state.pageToRender = initialState.pageToRender;
        },
        resetGetSortTypesOutput: (state) => {
            state.getSortTypesOutput = initialState.getSortTypesOutput;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(getSortTypes.pending, (state) => {
                state.getSortTypesOutput = undefined;
            })
            .addCase(getSortTypes.fulfilled, (state, action) => {
                state.getSortTypesOutput = action.payload;
            })
            .addCase(getSortTypes.rejected, (state) => {
                state.getSortTypesOutput = undefined;
            })
    }
});

export const {
    setPageToRender,
    setSelectedSortType,
    resetSortationPageToRender,
    resetGetSortTypesOutput,
} = sortContainerSlice.actions;
export default sortContainerSlice.reducer;